// SPDX-License-Identifier: AGPL-3.0-or-later

import * as ShopierCommands from '@app/features/premium/commands/ShopierCommands';
import type {ShopierStoreResponse} from '@fluxer/schema/src/domains/premium/ShopierSchemas';
import {useCallback, useEffect, useState} from 'react';

export type StoreItemKind = 'nameplate' | 'avatar_decoration' | 'profile_effect';

type StoreAccessStatus = 'loading' | 'ready' | 'failed';

let lastStore: ShopierStoreResponse | null = null;

let lastFetchedAt = 0;
let prefetchInFlight: Promise<void> | null = null;
const PREFETCH_FRESH_MS = 5 * 60_000;

/** Lets the store page share fresh ownership data with the profile pickers. */
export function rememberStore(store: ShopierStoreResponse): void {
	lastStore = store;
	lastFetchedAt = Date.now();
}

export function getRememberedStore(): ShopierStoreResponse | null {
	return lastStore;
}

/** Warms the store in the background so opening the store page renders immediately. */
export function prefetchStore(): void {
	if (prefetchInFlight || (lastStore && Date.now() - lastFetchedAt < PREFETCH_FRESH_MS)) return;
	prefetchInFlight = ShopierCommands.fetchStore()
		.then(rememberStore)
		.catch(() => undefined)
		.finally(() => {
			prefetchInFlight = null;
		});
}

/**
 * Store ownership for the profile pickers. While the first response is loading nothing is shown,
 * so the picker never flashes items the user cannot use. The last response is kept in memory so
 * reopening a picker is instant (it still refreshes in the background). If the store cannot be
 * loaded at all, or the catalog has no store fields yet, every item follows the old Pro rule.
 */
export function useStoreAccess(enabled = true) {
	const [store, setStore] = useState<ShopierStoreResponse | null>(lastStore);
	const [status, setStatus] = useState<StoreAccessStatus>(lastStore ? 'ready' : 'loading');
	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		ShopierCommands.fetchStore()
			.then((next) => {
				lastStore = next;
				if (cancelled) return;
				setStore(next);
				setStatus('ready');
			})
			.catch(() => {
				if (!cancelled && !lastStore) setStatus('failed');
			});
		return () => {
			cancelled = true;
		};
	}, [enabled]);
	const canUse = useCallback(
		(kind: StoreItemKind, id: string): boolean => {
			if (status === 'loading') return false;
			if (!store || !store.store_mode) return true;
			const item = store.items.find((candidate) => candidate.kind === kind && candidate.id === id);
			if (!item) return false;
			return item.owned || (item.pro_included && store.is_premium);
		},
		[store, status],
	);
	const ownsAny = (kind: StoreItemKind): boolean =>
		Boolean(store?.store_mode && store.items.some((item) => item.kind === kind && item.owned));
	return {store, canUse, ownsAny, loading: status === 'loading', storeMode: store?.store_mode === true};
}
